import { useMemo, useRef, useState } from 'react';
import { type AgentPolicy } from '@repo/api/schemas/agentPolicy.schema';
import {
  Button,
  ButtonIcon,
  Icon,
  Loader,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import { FormattedMessage, useIntl } from 'react-intl';

import { TOKENS } from '../../../../shared/constants';
import { formatTimestamp } from '../../../../shared/utils';
import { RowDash } from '../../../manage';
import {
  useIdentityOptions,
  useUpdatePolicy,
} from '../../hooks/useAgentPolicy';
import { getTriggerLabels } from '../../utils/ruleSummary';
import BulletLabel from '../BulletLabel/BulletLabel';
import { StatusChip } from '../PolicyPageTable/PolicyPageTable';

import './PolicyPageSidePanel.css';

const COLLAPSED_COUNT = 3;

function CollapsibleList({ items }: { items: string[] }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, COLLAPSED_COUNT);
  const hasMore = items.length > COLLAPSED_COUNT;

  return (
    <ul className='CollapsibleList'>
      {visible.map((line, idx) => (
        <li key={idx}>
          <BulletLabel size='xxSmall'>
            <Typography kind='body2' desktop='body2-d' mobile='body2-d'>
              {line}
            </Typography>
          </BulletLabel>
        </li>
      ))}
      {hasMore && (
        <li>
          <button
            className='CollapsibleList-toggleBtn'
            onClick={() => setExpanded((prev) => !prev)}>
            {expanded ? (
              <FormattedMessage id='gKIlkq' defaultMessage='...less' />
            ) : (
              <FormattedMessage
                id='kK6AGX'
                defaultMessage='...{count} more'
                values={{ count: items.length - COLLAPSED_COUNT }}
              />
            )}
          </button>
        </li>
      )}
    </ul>
  );
}

const LineItem = ({
  label,
  children,
}: {
  label: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className='SidePanelPolicyOverview-cardLineItem'>
    <Typography kind='h4' color={TOKENS.CF_NEU_BLD}>
      {label}
    </Typography>
    {children}
  </div>
);

const InfoField = ({
  label,
  value,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
}) => (
  <LineItem label={label}>
    <Typography kind='body2' color={TOKENS.CF_SEC_BLD}>
      {value || <RowDash />}
    </Typography>
  </LineItem>
);

export function PolicyOverview({
  policy,
  onPolicyUpdate,
  onStatusChange,
}: {
  policy: AgentPolicy;
  onPolicyUpdate: (data: Partial<AgentPolicy>) => void;
  onStatusChange?: (message: string) => void;
}) {
  const intl = useIntl();
  const pendingIsActiveRef = useRef<boolean | null>(null);
  const updatePolicyMutation = useUpdatePolicy({
    onError: () => {},
    onSuccess: (payload) => {
      onPolicyUpdate(payload);
      if (pendingIsActiveRef.current !== null) {
        const message = pendingIsActiveRef.current
          ? intl.formatMessage({
              id: '6+IOVH',
              defaultMessage: 'Rule activated successfully.',
              description: 'Success banner on policy activation',
            })
          : intl.formatMessage({
              id: 'Q6vL8h',
              defaultMessage: 'Rule deactivated successfully.',
              description: 'Success banner on policy deactivation',
            });
        onStatusChange?.(message);
        pendingIsActiveRef.current = null;
      }
    },
  });

  const handleBtnClick = (policy: AgentPolicy) => {
    const { id: policyId, isActive } = policy;
    pendingIsActiveRef.current = !isActive;
    updatePolicyMutation.mutate({
      policyId,
      payload: {
        isActive: !isActive,
      },
    });
  };

  return (
    <div className='SidePanelPolicyOverview-cardWrapper'>
      <Typography
        kind='h3'
        desktop='h3-d'
        mobile='h3-d'
        color={TOKENS.CF_NEU_BLD}>
        <FormattedMessage id='hVcbf7' defaultMessage='Rule Overview' />
      </Typography>

      <InfoField
        label={<FormattedMessage id='HAlOn1' defaultMessage='Name' />}
        value={policy.name}
      />
      <InfoField
        label={<FormattedMessage id='Q8Qw5B' defaultMessage='Description' />}
        value={policy.description}
      />
      <LineItem
        label={<FormattedMessage id='tzMNF3' defaultMessage='Status' />}>
        <div className='SidePanelPolicyOverview-statusValue'>
          <Typography kind='body2' color={TOKENS.CF_SEC_BLD}>
            <StatusChip isActive={policy.isActive} />
          </Typography>
          {policy.isActive ? (
            <Button
              kind='outlined'
              size='medium'
              leftIcon={<Icon kind='Reject' />}
              onClick={() => handleBtnClick(policy)}
              className='SidePanelPolicyOverview-statusBtn'>
              <FormattedMessage defaultMessage='De-activate' id='O27dTh' />
            </Button>
          ) : (
            <Button
              kind='outlined'
              size='medium'
              leftIcon={<Icon kind='Check' />}
              onClick={() => handleBtnClick(policy)}
              className='SidePanelPolicyOverview-statusBtn'>
              <FormattedMessage defaultMessage='Activate' id='AcxDgz' />
            </Button>
          )}
        </div>
      </LineItem>

      <InfoField
        label={<FormattedMessage id='nI+6ln' defaultMessage='Last Used on' />}
        value={formatTimestamp(policy.lastUsedTs)}
      />

      <InfoField
        label={<FormattedMessage id='EeRwjE' defaultMessage='Last Updated' />}
        value={formatTimestamp(policy.updatedTs)}
      />

      <InfoField
        label={
          <FormattedMessage id='MbkIAs' defaultMessage='Last Updated By' />
        }
        value={policy.lastUpdatedBy}
      />

      <InfoField
        label={<FormattedMessage id='97s2+w' defaultMessage='Created on' />}
        value={formatTimestamp(policy.createdTs)}
      />

      <InfoField
        label={<FormattedMessage id='p4mBmL' defaultMessage='Created by' />}
        value={policy.createdBy}
      />
    </div>
  );
}

export function RuleSummary({
  policy,
  identityMap,
}: {
  policy: AgentPolicy;
  identityMap: Record<string, string>;
}) {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(true);
  const trigger =
    getTriggerLabels(intl)[policy.triggerType] || policy.triggerType;

  const operatorLabels: Record<string, string> = {
    eq: intl.formatMessage({
      id: '1OTw2a',
      defaultMessage: 'equals',
      description: 'Operator label: equals',
    }),
    neq: intl.formatMessage({
      id: 'WDtQYQ',
      defaultMessage: 'does not equal',
      description: 'Operator label: does not equal',
    }),
    contains: intl.formatMessage({
      id: 'f8vxZn',
      defaultMessage: 'contains',
      description: 'Operator label: contains',
    }),
  };

  const getConditionLines = () => {
    const conds = policy.conditions?.conditions || [];
    return conds.map((c) => {
      if (typeof c === 'string') {
        return c;
      }
      const op = operatorLabels[c.operator] || c.operator;
      return `${c.field} ${op} ${c.value}`;
    });
  };

  const getActionLines = () => {
    const lines: string[] = [];
    const actions = policy.actions || [];
    actions.forEach((action) => {
      (action.payload || []).forEach((p) => {
        if (Array.isArray(p.value)) {
          p.value.forEach((v) => {
            const role = v.role || '';
            const rank = v.rank;
            const userId = v.assigned_to?.user_id;
            const assignedTo = userId
              ? (identityMap[userId] ?? userId)
              : v.assigned_to?.expression || v.assigned_to?.type || '';
            if (role) {
              lines.push(
                intl.formatMessage(
                  {
                    id: 'HYAbE0',
                    defaultMessage:
                      '{role} (Rank {rank}), Assigned to: {assignedTo}',
                    description:
                      'Action line in rule summary showing owner assignment',
                  },
                  { role, rank, assignedTo }
                )
              );
            }
          });
        }
      });
    });
    return lines;
  };

  const conditionLines = getConditionLines();
  const actionLines = getActionLines();

  const chevRonIcon = useMemo(() => {
    return (
      <Tooltip
        text={
          isExpanded
            ? intl.formatMessage({ defaultMessage: 'Collapse', id: 'W/V6+Y' })
            : intl.formatMessage({ defaultMessage: 'Expand', id: '0oLj/t' })
        }
        placement='top'
        trigger={
          <ButtonIcon
            size='large'
            kind='ghost'
            icon={
              <Icon
                kind={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                size='medium'
                className='Accordion-header-arrow'
                color={TOKENS.CF_NEU_BLD}
              />
            }
            onClick={() => {
              setIsExpanded((prev) => !prev);
            }}
            ariaLabel={intl.formatMessage({
              id: 'UsA/SO',
              defaultMessage: 'Rule summary accordion button',
              description: 'Accessibility label for rule summary accordion',
            })}
          />
        }
      />
    );
  }, [isExpanded]);

  return (
    <div className='SidePanelPolicyRuleSummary-cardWrapper'>
      <div className='SidePanelPolicyRuleSummary-titleRow'>
        <Typography kind='h3' desktop='h3-d' mobile='h3-d'>
          <FormattedMessage
            defaultMessage='Rule Summary'
            id='FwVvs0'
            description='Heading for rule summary section in policy details side panel'
          />
        </Typography>
        <div className='SidePanelPolicyRuleSummary-titleSpacer'>
          {chevRonIcon}
        </div>
      </div>
      <div
        className='SidePanelPolicyRuleSummary-bodyWrapper'
        data-open={isExpanded}
        aria-hidden={!isExpanded}
        {...({ inert: !isExpanded ? '' : undefined } as Record<
          string,
          string | undefined
        >)}>
        <div className='SidePanelPolicyRuleSummary-body'>
          <div className='SidePanelPolicyRuleSummary-section'>
            <Typography kind='h4' desktop='h4-d' mobile='h4-d'>
              <FormattedMessage
                defaultMessage='Trigger'
                id='PMrkuY'
                description='Heading for trigger section in rule summary of policy details side panel'
              />
            </Typography>
            <Typography kind='body2' desktop='body2-d' mobile='body2-d'>
              {intl.formatMessage(
                {
                  id: '/QmptU',
                  defaultMessage: 'This rule runs when: {trigger}',
                },
                { trigger }
              )}
            </Typography>
          </div>
          <div className='SidePanelPolicyRuleSummary-section'>
            <Typography kind='h4' desktop='h4-d' mobile='h4-d'>
              <FormattedMessage
                defaultMessage='Conditions'
                id='dwhuVp'
                description='Heading for conditions section in rule summary of policy details side panel'
              />
            </Typography>
            <Typography kind='body2' desktop='body2-d' mobile='body2-d'>
              <FormattedMessage
                defaultMessage='This rule applies when agents match:'
                id='wvVfjG'
                description='Description for conditions section in rule summary of policy details side panel'
              />
            </Typography>
            <CollapsibleList items={conditionLines} />
          </div>
          <div className='SidePanelPolicyRuleSummary-section'>
            <Typography kind='h4' desktop='h4-d' mobile='h4-d'>
              <FormattedMessage
                defaultMessage='Actions'
                id='TddbAM'
                description='Heading for actions section in rule summary of policy details side panel'
              />
            </Typography>
            <Typography kind='body2' desktop='body2-d' mobile='body2-d'>
              <FormattedMessage
                defaultMessage='Assign owners:'
                id='wmMkrW'
                description='Description for actions section in rule summary of policy details side panel'
              />
            </Typography>
            <CollapsibleList items={actionLines} />
          </div>
        </div>
      </div>
    </div>
  );
}

export function PoliciesSidePanelContent({
  policy,
  onPolicyUpdate,
  onStatusChange,
}: {
  policy: AgentPolicy;
  onPolicyUpdate: (data: Partial<AgentPolicy>) => void;
  onStatusChange?: (message: string) => void;
}) {
  // Extract owner emails from policy actions to resolve display names
  const ownerEmails = useMemo(() => {
    const emails: string[] = [];
    (policy.actions || []).forEach((action) => {
      (action.payload || []).forEach((p) => {
        if (Array.isArray(p.value)) {
          p.value.forEach((v) => {
            const email = v.assigned_to?.user_id;
            if (email) {
              emails.push(email);
            }
          });
        }
      });
    });
    return emails;
  }, [policy.actions]);

  const { data: identityOptions = [], isLoading: isResolvingOwners } =
    useIdentityOptions({
      pageSize: 100,
      enabled: ownerEmails.length > 0,
      filters:
        ownerEmails.length > 0
          ? [{ field: 'email', operator: 'IN', values: ownerEmails }]
          : [],
    });

  // Map email → display name for owner resolution
  const identityMap = useMemo(() => {
    const map: Record<string, string> = {};
    identityOptions.forEach((opt) => {
      if (opt.secondaryLabel) {
        map[opt.secondaryLabel] = opt.label;
      }
    });
    return map;
  }, [identityOptions]);

  if (ownerEmails.length > 0 && isResolvingOwners) {
    return (
      <div className='SidePanelPolicyRuleSummary-loaderRow'>
        <Loader kind='dots' format='inline' color='brand' />
      </div>
    );
  }

  return (
    <>
      <RuleSummary policy={policy} identityMap={identityMap} />
      <PolicyOverview
        policy={policy}
        onPolicyUpdate={onPolicyUpdate}
        onStatusChange={onStatusChange}
      />
    </>
  );
}
